/**
 * RemoteApp — the Spot-Remote role. Enter the share key shown on the TV, pair, and
 * drive the room over the room-do control plane (zero XMPP):
 *
 *   entry    — type the share key -> POST /pair -> access token + room.
 *   control  — connect to the room-do WS (`?token=`), render the control bar +
 *              in-call toolbar, and send `ControlEnvelope` command frames whose
 *              correlated `ack` confirms the TV acted.
 *
 * The toolbar is driven entirely by the TV's broadcast presence (button states,
 * and the moderator-gated record button — shown only while the TV reports
 * `isLocalModerator`). This is the spot-client Remote, re-transported.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage, MeetingParticipant, MessageEnvelope, RoomOccupant, SpotTvPresence } from '@spaces/contracts';
import { COMMANDS } from '@spaces/contracts';
import { SET_RECORDING, type ClientCommandName } from '../commands.js';
import { getConfig } from '../config.js';
import { getDeviceId } from '../device-id.js';
import { log } from '../log.js';
import { meetingNameForRoom } from '../meeting-name.js';
import { canScreenShareHere, ScreenshareSession } from '../share/screenshare-session.js';
import { PairingClient, PairingError } from '../transport/pairing-client.js';
import { RemoteControlService } from '../transport/RemoteControlService.js';
import { Screen } from '../ui/components.js';
import { RemoteEntry } from './RemoteEntry.js';
import { RemoteHome } from './RemoteHome.js';
import { RemoteInCall } from './RemoteInCall.js';

type Phase = 'entry' | 'pairing' | 'control' | 'error';

function spotIdOf(occupants: RoomOccupant[]): string {
    return occupants.find(o => o.clientType === 'spot-tv')?.endpointId ?? '';
}

export function RemoteApp(): JSX.Element {
    const config = useMemo(() => getConfig(), []);
    const deviceId = useMemo(() => getDeviceId('remote'), []);
    // Same-origin pairing proxy (see TvApp); room-do WS stays a direct connect.
    const pairing = useMemo(() => new PairingClient('/api'), []);

    const [phase, setPhase] = useState<Phase>('entry');
    const [status, setStatus] = useState<string>('Enter the share key shown on the room TV');
    const [keyInput, setKeyInput] = useState<string>('');
    const [presence, setPresence] = useState<SpotTvPresence | null>(null);
    const [lastAck, setLastAck] = useState<string>('');
    const [tvConnected, setTvConnected] = useState<boolean>(false);
    const [roomName, setRoomName] = useState<string>('');
    // The share key this Remote paired with — surfaced in the in-call Meeting info panel.
    const [pairedKey, setPairedKey] = useState<string>('');
    // The room's 8x8 meeting-tenant slug (e.g. "emchodemo"), from the pair response.
    // Drives the "Join with ID" prefix (8x8.vc/<tenant>/) and the Meeting-info join link.
    const [meetingTenant, setMeetingTenant] = useState<string>('');
    // Optimistic whiteboard state: Jitsi emits no whiteboard-status event, so the TV
    // can't relay it in presence — the Remote tracks the toggle locally.
    const [whiteboardOpen, setWhiteboardOpen] = useState<boolean>(false);
    // True while THIS Remote (on a laptop) is proxying its own screen into the room.
    // Drives the Share button's "Stop" affordance even when the TV's presence echo
    // (presence.screensharing) hasn't flipped — a remote-proxied "content" share is a
    // separate participant, so the TV's own screenSharingStatusChanged need not fire.
    const [localSharing, setLocalSharing] = useState<boolean>(false);

    const rcsRef = useRef<RemoteControlService | null>(null);
    const roomIdRef = useRef<string>('');
    // The room-scoped access token from the pair response — kept so the Remote can
    // make its own same-origin roomkeeper calls (the Calendar tile's agenda fetch),
    // exactly as the TV keeps `sessionRef.current.accessToken`. It's otherwise handed
    // to RemoteControlService / ScreenshareSession and would be dropped here.
    const accessTokenRef = useRef<string>('');
    // The Spot-TV's room endpoint id (from the roster) and the laptop-share session —
    // populated only on a desktop Remote that can capture a screen (see canShareHere).
    const spotIdRef = useRef<string>('');
    const shareSessionRef = useRef<ScreenshareSession | null>(null);
    // Can this device capture a screen at all? Desktop browser → yes (real laptop
    // share); in-room iPad / phone → no (keep the "share from your laptop" hint).
    const canShareHere = useMemo(() => canScreenShareHere(), []);

    const send = useCallback(async (command: ClientCommandName, payload?: unknown) => {
        const rcs = rcsRef.current;

        if (!rcs) {
            return;
        }

        setLastAck(`→ ${String(command)}…`);

        try {
            const ack = await rcs.sendCommand(command, payload);

            setLastAck(ack.ok ? `✓ ${String(command)}` : `✗ ${String(command)}: ${ack.error ?? 'rejected'}`);
            log.log('Remote command ack', { command, ok: ack.ok });
        } catch (err) {
            setLastAck(`✗ ${String(command)}: ${(err as Error).message}`);
            log.error('Remote command failed', { command, error: String(err) });
        }
    }, []);

    /**
     * Share, capability-branched (Emil's model):
     *
     * - **In-room iPad / phone** (no `getDisplayMedia`): the room TV has no meaningful
     *   local screen and a tablet can't capture one, so Share just surfaces the
     *   guidance to share from a laptop. The "Sharing" indicator still lights from the
     *   TV's real `presence.screensharing` when some OTHER laptop is proxying in.
     *
     * - **Desktop browser** (this Remote IS running on a laptop, e.g. share.8x8.vc on a
     *   computer): Share captures the screen (`getDisplayMedia`) and proxies it into the
     *   room's meeting over the SAME control channel this Remote is already on — the
     *   identical legacy-Spot screenshare-only "content" flow the `/share` page uses,
     *   so the chosen screen lands on the room TV's stage. A second tap stops it.
     */
    const onShare = useCallback(() => {
        if (!canShareHere) {
            setLastAck(`ℹ To share, open ${config.shareHost} on your laptop and enter the room code`);

            return;
        }

        const session = shareSessionRef.current;

        if (!session) {
            return;
        }

        // Already starting or live → this tap stops it (the button shows "Stop").
        if (session.state !== 'idle') {
            session.stop('user-stopped');
            setLastAck('Screen sharing stopped');

            return;
        }

        const spotId = spotIdRef.current;

        if (!spotId) {
            setLastAck('The room TV is not connected yet — try again in a moment');

            return;
        }

        void session.start(spotId);
    }, [canShareHere, config.shareHost]);

    const doPair = useCallback(async (key: string) => {
        const trimmed = key.trim();

        if (!trimmed) {
            return;
        }

        setPhase('pairing');
        setStatus('Pairing…');

        try {
            const pair = await pairing.pair(trimmed, deviceId);

            roomIdRef.current = pair.room.id;
            accessTokenRef.current = pair.accessToken;

            const rcs = new RemoteControlService({
                roomDoUrl: config.roomDoUrl,
                roomId: pair.room.id,
                endpointId: deviceId,
                accessToken: pair.accessToken,
                clientType: 'remote-temp',
                joinCode: trimmed,
                // Retry forever, like the TV and legacy spot's reconnect loop: a room
                // Remote should ride out any drop/sleep, not give up after a bounded
                // budget. Its access token is long-lived (24h, matching prod), so a
                // reconnect always carries a valid credential — no refresh needed.
                maxReconnectAttempts: Number.POSITIVE_INFINITY
            });

            rcs.on('presence', status => {
                setPresence(status);
                setTvConnected(true);
            });
            rcs.on('roster', occupants => {
                spotIdRef.current = spotIdOf(occupants);
                setTvConnected(occupants.some(o => o.clientType === 'spot-tv'));
            });
            // Inbound wireless-screenshare proxy replies from the TV (only meaningful on
            // a desktop Remote that is sharing — handleMessage ignores everything else).
            rcs.on('message', (frame: MessageEnvelope) => shareSessionRef.current?.handleMessage(frame));
            rcs.on('error', frame => setStatus(`Room error: ${frame.message}`));
            // A transient drop: the transport is auto-reconnecting. STAY on the control
            // screen — the existing "Reconnecting to the room…" indicator (driven by
            // tvConnected) tells the user; a fresh presence on reconnect clears it.
            rcs.on('reconnecting', info => {
                log.warn('Remote reconnecting', info);
                setTvConnected(false);
            });
            rcs.on('reconnected', () => {
                log.log('Remote reconnected');
            });
            // Terminal only: the transport exhausted its retries (or the server closed
            // us out). Now we drop to the re-pair screen.
            rcs.on('disconnect', info => {
                log.warn('Remote disconnected', info);
                setStatus('Disconnected from the room');
                setPhase('error');
            });

            await rcs.connect();
            rcsRef.current = rcs;

            // Only a screen-capable Remote (a laptop) gets a share session; the in-room
            // iPad never captures, so it never holds one (Share stays a hint there).
            if (canShareHere) {
                shareSessionRef.current = new ScreenshareSession({
                    externalApiSrc: config.externalApiSrc,
                    deviceId,
                    rcs,
                    accessToken: pair.accessToken,
                    onState: state => setLocalSharing(state !== 'idle'),
                    onStatus: setLastAck
                });
            }

            setRoomName(pair.room.name);
            setPairedKey(trimmed);
            setMeetingTenant(pair.meetingTenant);
            setPhase('control');
            setStatus(`Paired with ${pair.room.name}`);
            log.log('Remote paired', { roomId: pair.room.id });
        } catch (err) {
            const message = err instanceof PairingError ? `${err.message} (${err.code})` : (err as Error).message;

            log.error('Remote pairing failed', { error: message });
            setStatus(`Pairing failed: ${message}`);
            setPhase('entry');
        }
    }, [canShareHere, config.externalApiSrc, config.roomDoUrl, deviceId, pairing]);

    useEffect(() => {
        // Allow a deep-linked key: /remote-control?key=XXXXXX or ?share=XXXXXX
        const params = new URLSearchParams(window.location.search);
        const urlKey = params.get('key') ?? params.get('share');

        if (urlKey) {
            setKeyInput(urlKey);
            void doPair(urlKey);
        }
    }, []);

    useEffect(() => () => {
        shareSessionRef.current?.stop('unmount');
        rcsRef.current?.disconnect();
    }, []);

    const inMeeting = Boolean(presence && presence.view === 'meeting' && presence.inMeeting);
    const audioMuted = Boolean(presence?.audioMuted);
    const videoMuted = Boolean(presence?.videoMuted);
    const tileView = Boolean(presence?.tileView);
    const recording = Boolean(presence?.recording);
    const isModerator = Boolean(presence?.isLocalModerator);
    const handRaised = Boolean(presence?.handRaised);
    const needPassword = Boolean(presence?.needPassword);
    // The room is sharing when the TV reports a screenshare in its meeting (some other
    // laptop on the `/share` page), OR when THIS desktop Remote is itself proxying its
    // screen in (localSharing) — the latter need not echo back in the TV's presence.
    const screensharing = Boolean(presence?.screensharing) || localSharing;
    const participants: MeetingParticipant[] = presence?.participants ?? [];
    // Chat: whether the room's chat panel is open on the TV (drives the open/close
    // toggle), and the recent thread the TV relays so the Remote can read it.
    const chatPanelOpen = Boolean(presence?.chatPanelOpen);
    const chatThread: ChatMessage[] = presence?.chatThread ?? [];

    // Leaving the meeting clears the optimistic whiteboard latch and stops any share
    // this Remote was driving (the proxied screen dies with the meeting anyway).
    useEffect(() => {
        if (!inMeeting) {
            setWhiteboardOpen(false);
            shareSessionRef.current?.stop('left-meeting');
        }
    }, [inMeeting]);

    if (phase === 'control' && inMeeting) {
        const displayMeeting = (typeof presence?.inMeeting === 'string' && presence.inMeeting)
            || meetingNameForRoom(roomIdRef.current);

        return (
            <Screen role = "remote">
                <RemoteInCall
                    roomName = { roomName }
                    meetingName = { displayMeeting }
                    meetingDomain = { config.meetingDomain }
                    shareHost = { config.shareHost }
                    shareKey = { pairedKey }
                    tvConnected = { tvConnected }
                    isModerator = { isModerator }
                    audioMuted = { audioMuted }
                    videoMuted = { videoMuted }
                    tileView = { tileView }
                    recording = { recording }
                    screensharing = { screensharing }
                    handRaised = { handRaised }
                    needPassword = { needPassword }
                    participants = { participants }
                    chatPanelOpen = { chatPanelOpen }
                    chatThread = { chatThread }
                    lastAck = { lastAck }
                    onMute = { () => void send(COMMANDS.SET_AUDIO_MUTE, { mute: !audioMuted }) }
                    onVideo = { () => void send(COMMANDS.SET_VIDEO_MUTE, { mute: !videoMuted }) }
                    onLayout = { tile => void send(COMMANDS.SET_TILE_VIEW, { tileView: tile }) }
                    onRecord = { on => void send(SET_RECORDING, { on }) }
                    onRaiseHand = { raised => void send(COMMANDS.SET_RAISE_HAND, { raised }) }
                    onWhiteboard = { () => {
                        const next = !whiteboardOpen;

                        setWhiteboardOpen(next);
                        void send(COMMANDS.SET_WHITEBOARD, { visible: next });
                    } }
                    onShare = { onShare }
                    onChat = { text => void send(COMMANDS.SEND_CHAT_MESSAGE, { text }) }
                    onSetChatOpen = { open => void send(COMMANDS.SET_CHAT_OPEN, { open }) }
                    onCaptions = { on => void send(COMMANDS.SET_SUBTITLES, { on }) }
                    onPin = { participantId => void send(COMMANDS.PIN_PARTICIPANT, { participantId }) }
                    onMuteAll = { () => void send(COMMANDS.MUTE_EVERYONE, { mediaType: 'audio' }) }
                    onLobby = { enabled => void send(COMMANDS.SET_LOBBY, { enabled }) }
                    onTone = { digit => void send(COMMANDS.SEND_TOUCH_TONES, { tones: digit }) }
                    onSubmitPassword = { password => void send(COMMANDS.SUBMIT_PASSWORD, { password }) }
                    onHangup = { () => void send(COMMANDS.HANG_UP, { skipFeedback: true }) } />
            </Screen>
        );
    }

    if (phase === 'control') {
        return (
            <Screen role = "remote">
                <RemoteHome
                    roomName = { roomName }
                    roomId = { roomIdRef.current }
                    accessToken = { accessTokenRef.current }
                    tvConnected = { tvConnected }
                    isModerator = { isModerator }
                    meetingDomain = { config.meetingDomain }
                    meetingTenant = { meetingTenant }
                    canShareHere = { canShareHere }
                    onJoin = { () => void send(COMMANDS.GO_TO_MEETING, { meetingName: meetingNameForRoom(roomIdRef.current) }) }
                    onJoinWithId = { name => void send(COMMANDS.GO_TO_MEETING, { meetingName: name }) }
                    onDial = { number => void send(COMMANDS.GO_TO_MEETING, {
                        meetingName: meetingNameForRoom(roomIdRef.current),
                        invites: [{ type: 'phone', number }]
                    }) }
                    onShare = { onShare } />
            </Screen>
        );
    }

    return (
        <Screen role = "remote">
            <RemoteEntry
                status = { status }
                tone = { phase === 'error' ? 'err' : 'info' }
                keyInput = { keyInput }
                pairing = { phase === 'pairing' }
                onChange = { setKeyInput }
                onConnect = { () => void doPair(keyInput) } />
        </Screen>
    );
}
